import React, { lazy, useEffect, useState } from 'react';
import { styled } from 'linaria/react';
import t from '@jetshop/intl';
import { IntroStep } from './Steps/IntroStep';
import { CategoryStep } from './Steps/CategoryStep';
import { ProductStep } from './Steps/ProductStep';
import { ColorStep } from './Steps/ColorStep';
import { LegStep } from './Steps/LegStep';
import { SummaryStep } from './Steps/SummaryStep';
import { SummarySharedStep } from './Steps/SummarySharedStep';
import { Main3DButton } from './Elements/Texts/Button3D';
import { useLocation } from 'react-router-dom';
import productsConfig from './Config/products.config';

const Main3DWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  margin: 0 auto 4rem;
  max-width: 80rem;
  width: 100%;
  padding: 40px 60px;
  text-align: center;
  overflow: hidden;

  &.full {
    max-width: 100%;
    padding: 0 0;
  }

  @media screen and (max-width: 1023px) {
    padding: 0 10px 0;
  }
  @media screen and (max-width: 600px) {
    max-width: 100%;
  }
`;

const StyledButtonWrapper = styled('div')`
  text-align: center;
`;

const Main3D = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [activeProduct, setActiveProduct] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTextileIndex, setActiveTextileIndex] = useState(null);
  const [activeLeg, setActiveLeg] = useState(null);
  const [config, setConfig] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [charpstARConfig, setCharpstARConfig] = useState(null);

  const steps = [
    { id: 1, title: t('Designa din egen soffa'), content: IntroStep },
    { id: 2, title: t('Typ av soffa'), content: CategoryStep },
    { id: 3, title: t('Soffa'), content: ProductStep },
    { id: 4, title: t('Färg och tyg'), content: ColorStep },
    { id: 5, title: t('Ben'), content: LegStep },
    { id: 6, title: t('Sammanfattning'), content: SummaryStep },
  ];

  const search = useLocation().search;
  const id = new URLSearchParams(search).get('config');

  useEffect(() => {
    (async () => {
      try {
        // Note: This only works if the script is an ES module AND your bundler allows it!
        const module = await import(
          /* webpackIgnore: true */ 'https://js.charpstar.net/MobelMastarna/charpstAR-MobelMastarna-v2.js'
        );
        setCharpstARConfig(module.default);

        // Use module exports here
      } catch (error) {
        console.error('Failed to load module:', error);
      }
    })();
  }, [charpstARConfig]);

  useEffect(() => {
    const script2 = document.createElement('script');
    script2.src = 'https://js.charpstar.net/MobelMastarna/model-viewer.js';
    script2.type = 'module';
    if (!document.body.contains(script2)) {
      document.body.appendChild(script2);
    }
  }, []);
  useEffect(() => {
    const clearTextiles = () => {
      sessionStorage.removeItem('mobelmastarna_3d_textiles');
    };
    const clearLegs = () => {
      sessionStorage.removeItem('mobelmastarna_3d_legs');
    };
    const lastStep = () => {
      sessionStorage.removeItem('lastStep');
    };
    window.addEventListener('beforeunload', clearTextiles);
    window.addEventListener('beforeunload', clearLegs);
    window.addEventListener('beforeunload', lastStep);
    return () => {
      window.removeEventListener('beforeunload', clearTextiles);
      window.removeEventListener('beforeunload', clearLegs);
      window.removeEventListener('beforeunload', lastStep);
    };
  }, []);
  useEffect(() => {
    if (charpstARConfig === null) return;
    setIsClient(true);
    window.charpstARConfig = charpstARConfig;
  }, [charpstARConfig]);

  useEffect(() => {
    if (!id) {
      setConfig(null);
      return;
    }
    const decoded = Buffer.from(id, 'base64').toString('utf-8');
    try {
      const parsedConfig = JSON.parse(decoded);
      setConfig(parsedConfig);
    } catch (e) {
      console.error('Error parsing config:', e);
      setConfig(null);
    }
  }, [id]);

  useEffect(() => {
    if (config === null) return;
    let product = productsConfig.find(
      (prod) =>
        prod.defaultArticleNumber.trim() === config.defaultArticleNumber.trim()
    );
    product = { ...product, isShared: true };
    /*product.isShared = true
    product.selectedVariant = config.selectedVariant*/
    product = { ...product, selectedVariant: config.selectedVariant };
    if (
      product.legs &&
      Array.isArray(product.legs) &&
      product.legs.some(
        (leg) =>
          Array.isArray(leg.articleNumbers) &&
          leg.articleNumbers.includes(config.selectedVariant) &&
          leg.useDifferentArticle
      )
    ) {
      const matchingLeg = product.legs.find(
        (leg) =>
          Array.isArray(leg.articleNumbers) &&
          leg.articleNumbers.includes(config.selectedVariant) &&
          leg.useDifferentArticle
      );
      if (matchingLeg && matchingLeg.useDifferentArticle) {
        product.defaultArticleNumber = matchingLeg.useDifferentArticle;
      }
    }
    setActiveProduct(product);
    setActiveCategory(config.activeCategory);
    setActiveTextileIndex(config.activeTextileIndex);
    setActiveLeg(config.activeLeg);
    setCurrentStep(5);
  }, [config]);

  const StepComponent =
    activeProduct && activeProduct.isShared
      ? SummarySharedStep
      : steps[currentStep].content;

  if (!isClient || !window.charpstARConfig) {
    // Render a loading state or skeleton that matches the server markup
    return <Main3DWrapper className={currentStep > 2 ? 'full 3d' : '3d'} />;
  }

  if (!id || config === null) {
    return (
      <>
        <Main3DWrapper className={currentStep > 2 ? 'full 3d' : '3d'}>
          <StepComponent
            title={steps[currentStep].title}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            activeProduct={activeProduct}
            setActiveProduct={setActiveProduct}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            activeTextileIndex={activeTextileIndex}
            setActiveTextileIndex={setActiveTextileIndex}
            activeLeg={activeLeg}
            setActiveLeg={setActiveLeg}
          />
          {currentStep === 0 ? (
            <StyledButtonWrapper className={'button-wrapper'}>
              <Main3DButton
                text={'Börja designa din soffa'}
                onClick={() => setCurrentStep((i) => i + 1)}
              ></Main3DButton>
            </StyledButtonWrapper>
          ) : (
            ''
          )}
        </Main3DWrapper>
      </>
    );
  }

  return (
    <>
      <Main3DWrapper className={currentStep > 2 ? 'full 3d' : '3d'}>
        <StepComponent
          title={steps[currentStep].title}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          activeProduct={activeProduct}
          setActiveProduct={setActiveProduct}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          activeTextileIndex={activeTextileIndex}
          setActiveTextileIndex={setActiveTextileIndex}
          activeLeg={activeLeg}
          setActiveLeg={setActiveLeg}
        />
        {currentStep === 0 ? (
          <StyledButtonWrapper className={'button-wrapper'}>
            <Main3DButton
              text={'Börja designa din soffa'}
              onClick={() => setCurrentStep((i) => i + 1)}
            ></Main3DButton>
          </StyledButtonWrapper>
        ) : (
          ''
        )}
      </Main3DWrapper>
    </>
  );
};

export default Main3D;
